import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { ActiviteRow, CanalActivite } from '@/lib/database.types';

export function useActivites(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['activites', entrepriseId],
    enabled: !!entrepriseId,
    queryFn: async (): Promise<ActiviteRow[]> => {
      if (!entrepriseId) return [];
      const { data, error } = await supabase
        .from('activites')
        .select('*')
        .eq('entreprise_id', entrepriseId)
        .order('ordre')
        .order('nom');
      if (error) throw error;
      return data;
    },
  });
}

export function useAllActivites() {
  return useQuery({
    queryKey: ['activites', 'all'],
    queryFn: async (): Promise<ActiviteRow[]> => {
      const { data, error } = await supabase.from('activites').select('*').order('nom');
      if (error) throw error;
      return data;
    },
  });
}

export interface UpsertActiviteInput {
  id?: string;
  entreprise_id: string;
  code: string;
  nom: string;
  canal: CanalActivite;
  description?: string | null;
  responsable_id?: string | null;
  ordre?: number;
  is_active?: boolean;
}

export function useUpsertActivite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertActiviteInput): Promise<ActiviteRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('activites')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data;
      }
      const { data, error } = await supabase.from('activites').insert(rest).select('*').single();
      if (error) throw error;
      return data;
    },
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: ['activites', row.entreprise_id] });
      void qc.invalidateQueries({ queryKey: ['activites', 'all'] });
    },
  });
}

export function useDeleteActivite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('activites').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['activites'] });
    },
  });
}

// =====================================================================
// Sprint 6.9.5 — Bulk + check FK avant DELETE
// =====================================================================

export interface ActiviteFkUsage {
  activite_id: string;
  operations: number;
  paiements_formateurs: number;
  dettes_fournisseurs: number;
  recouvrement: number;
  total: number;
}

/** Compte les références FK pour décider si DELETE possible. */
export async function checkActiviteFkUsage(
  activiteIds: string[],
): Promise<Map<string, ActiviteFkUsage>> {
  const map = new Map<string, ActiviteFkUsage>();
  for (const id of activiteIds) {
    map.set(id, {
      activite_id: id,
      operations: 0,
      paiements_formateurs: 0,
      dettes_fournisseurs: 0,
      recouvrement: 0,
      total: 0,
    });
  }
  if (activiteIds.length === 0) return map;
  const tables: { name: string; key: keyof ActiviteFkUsage }[] = [
    { name: 'operations', key: 'operations' },
    { name: 'paiements_formateurs', key: 'paiements_formateurs' },
    { name: 'dettes_fournisseurs', key: 'dettes_fournisseurs' },
    { name: 'recouvrement_entreprises', key: 'recouvrement' },
  ];
  for (const t of tables) {
    const { data } = await supabase
      .from(t.name)
      .select('activite_id')
      .in('activite_id', activiteIds);
    for (const row of (data ?? []) as { activite_id: string }[]) {
      const rec = map.get(row.activite_id);
      if (rec) {
        const r = rec as unknown as Record<string, number>;
        const k = t.key as string;
        r[k] = (r[k] ?? 0) + 1;
        rec.total++;
      }
    }
  }
  return map;
}

export function useBulkActivateActivites() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ids: string[]; is_active: boolean }): Promise<void> => {
      if (input.ids.length === 0) return;
      const { error } = await supabase
        .from('activites')
        .update({ is_active: input.is_active })
        .in('id', input.ids);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['activites'] }),
  });
}

export function useBulkDeleteActivites() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      ids: string[],
    ): Promise<{ deleted: string[]; refused: { id: string; usage: ActiviteFkUsage }[] }> => {
      const usageMap = await checkActiviteFkUsage(ids);
      const deletable = ids.filter((id) => (usageMap.get(id)?.total ?? 0) === 0);
      const refused = ids
        .filter((id) => (usageMap.get(id)?.total ?? 0) > 0)
        .map((id) => ({ id, usage: usageMap.get(id) as ActiviteFkUsage }));
      if (deletable.length > 0) {
        const { error } = await supabase.from('activites').delete().in('id', deletable);
        if (error) throw error;
      }
      return { deleted: deletable, refused };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['activites'] }),
  });
}
